<template>
  <div class="new-energy-container">
    <!-- 顶部横幅 -->
    <div class="banner-section" :style="bannerStyle">
      <div class="banner-content">
        <h1 class="banner-title">{{ pageData.title || '新能源物流' }}</h1>
        <p class="banner-subtitle">{{ pageData.subTitle || '专业新能源物流解决方案，助力绿色能源发展' }}</p>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载数据...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <h3>加载失败</h3>
      <p>{{ error }}</p>
      <button @click="fetchData" class="retry-btn">重新加载</button>
    </div>

    <!-- 页面内容 -->
    <div v-else class="page-content">
      <!-- 业务介绍 -->
      <div class="intro-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">{{ introData.title || '新能源物流专业服务' }}</h2>
            <div class="title-decoration"></div>
          </div>
          <div class="section-content">
            <p>{{ introData.content || '我们专注于新能源汽车、光伏设备、储能系统等新能源产品的物流运输，提供专业的包装、运输、仓储一体化解决方案。凭借丰富的行业经验和专业团队，我们确保新能源产品安全高效地送达全球各地。' }}</p>
          </div>
        </div>
      </div>

      <!-- 服务优势 -->
      <div class="advantage-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">服务优势</h2>
            <div class="title-decoration"></div>
          </div>
          <div class="advantage-grid">
            <div class="advantage-item" v-for="(advantage, index) in advantageData" :key="index">
              <div class="advantage-icon">
                <i :class="getAdvantageIcon(index)"></i>
              </div>
              <div class="advantage-content">
                <h3>{{ advantage.title || `优势${index + 1}` }}</h3>
                <p>{{ advantage.description || '专业的服务优势描述' }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 服务内容 -->
      <div class="service-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">服务内容</h2>
            <div class="title-decoration"></div>
          </div>
          <div class="service-grid">
            <div class="service-item" v-for="(service, index) in serviceData" :key="index">
              <div class="service-icon">
                <i :class="getServiceIcon(index)"></i>
              </div>
              <div class="service-content">
                <h3>{{ service.title || `服务${index + 1}` }}</h3>
                <p>{{ service.description || '专业的服务内容描述' }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 联系我们 -->
    <CallMe />
    <MobileCallme />
  </div>
</template>

<script>
import CallMe from '@/components/callme/index'
import MobileCallme from '@/components/callme/MobileCallme.vue'
import { getNewEnergyCaseData } from '@/api/new-energy'

export default {
  name: 'NewEnergy',
  data() {
    return {
      pageData: {},
      introData: {},
      advantageData: [],
      serviceData: [],
      loading: true,
      error: null
    }
  },
  components: {
    CallMe,
    MobileCallme
  },
  computed: {
    bannerStyle() {
      if (this.pageData.bannerImage) {
        return {
          backgroundImage: `url(${this.pageData.bannerImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }
      }
      return {
        background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.9), rgba(52, 211, 153, 0.8))'
      }
    }
  },
  methods: {
    getAdvantageIcon(index) {
      const icons = ['el-icon-medal', 'el-icon-monitor', 'el-icon-user']
      return icons[index] || 'el-icon-star'
    },
    getServiceIcon(index) {
      const icons = ['el-icon-truck', 'el-icon-sunny', 'el-icon-battery']
      return icons[index] || 'el-icon-service'
    },
    async fetchData() {
      try {
        this.loading = true
        console.log('开始请求新能源案例数据...')
        // 获取当前语言
        const currentLanguage = localStorage.getItem('preferred-language') || 'zh-CN'
        const response = await getNewEnergyCaseData(currentLanguage)
        console.log('新能源案例API响应:', response)
        if (response.code === 200) {
          console.log('新能源案例数据获取成功')
          this.processData(response.data)
        } else {
          this.error = `获取数据失败: ${response.msg || '未知错误'}`
          console.error('新能源案例API返回错误:', response)
        }
      } catch (err) {
        this.error = `网络错误: ${err.message}`
        console.error('获取新能源案例数据失败:', err)
      } finally {
        this.loading = false
      }
    },
    processData(data) {
      // 新的API返回的是结构化数据，直接使用
      console.log('处理新能源案例数据:', data)
      
      // 页面基础数据
      this.pageData = data.pageData || {}
      
      // 业务介绍数据
      this.introData = data.introData || {}
      
      // 服务优势数据
      this.advantageData = data.advantageData || []
      
      // 服务内容数据
      this.serviceData = data.serviceData || []
    }
  },
  mounted() {
    this.fetchData()
  }
}
</script>

<style lang="scss" scoped>
.new-energy-container {
  position: relative;
  overflow-x: hidden;
}

// 横幅区域
.banner-section {
  width: 100%;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-align: center;
  position: relative;
}

.banner-content {
  max-width: 800px;
  padding: 0 20px;
}

.banner-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.5);
}

.banner-subtitle {
  font-size: 1.3rem;
  margin-bottom: 25px;
  opacity: 0.9;
}

// 通用容器
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

// 通用标题样式
.section-header {
  margin-bottom: 40px;
  text-align: center;
}

.section-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 15px;
}

.title-decoration {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #1a73e8, #34d399);
  border-radius: 2px;
  margin: 0 auto;
}

// 内容区域
.page-content {
  padding: 80px 0;
}

.intro-section,
.advantage-section,
.service-section {
  margin-bottom: 80px;
}

.section-content p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4a5568;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

// 优势网格
.advantage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.advantage-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 30px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease;
  text-align: center;
}

.advantage-item:hover {
  transform: translateY(-5px);
}

.advantage-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #1a73e8, #34d399);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  margin-bottom: 20px;
}

.advantage-content h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 15px;
}

.advantage-content p {
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
}

// 服务网格
.service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
}

.service-item {
  display: flex;
  align-items: flex-start;
  padding: 30px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease;
}

.service-item:hover {
  transform: translateY(-5px);
}

.service-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #1a73e8, #34d399);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin-right: 20px;
  flex-shrink: 0;
}

.service-content h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 10px;
}

.service-content p {
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  text-align: left;
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  text-align: center;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1a73e8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-container p {
  font-size: 1.1rem;
  color: #64748b;
}

// 错误状态
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 0;
  text-align: center;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.error-container h3 {
  font-size: 1.5rem;
  color: #dc2626;
  margin-bottom: 15px;
}

.error-container p {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 25px;
  max-width: 400px;
}

.retry-btn {
  background: linear-gradient(135deg, #1a73e8, #34d399);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.retry-btn:hover {
  transform: translateY(-2px);
}

// 动画效果
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 响应式设计
@media (max-width: 768px) {
  .banner-section {
    height: 350px;
    padding: 0 20px;
  }
  
  .banner-title {
    font-size: 2rem;
  }
  
  .banner-subtitle {
    font-size: 1rem;
  }
  
  .page-content {
    padding: 50px 0;
  }
  
  .intro-section,
  .advantage-section,
  .service-section {
    margin-bottom: 50px;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .advantage-grid,
  .service-grid {
    grid-template-columns: 1fr;
  }
  
  .service-item {
    flex-direction: column;
    text-align: center;
  }
  
  .service-icon {
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .service-content p {
    text-align: center;
  }
}
</style>
